import './BuyNowButton.css';

import { WorkflowVersion } from '@foxden/version-controller-client';
import { EmailOutlined, Warning } from '@material-ui/icons';
import React, { useContext, useState } from 'react';

import { PaymentGraphqlClient } from '../../../../backend-client/paymentGraphqlBackend';
import { RatingQuotingClient } from '../../../../backend-client/ratingQuotingBackend';
import Spinner from '../../../../components/Spinner';
import { BusinessInfo } from '../../../../generated/rating-quoting-graphql';
import Download from '../../../../images/Download.svg';
import getEnv from '../../../../utils/getEnv';
import { Context, ContextValue } from '../../../../utils/store';
import { NewQuoteForApp } from '../../Quote';
import { AdditionalInsuredInfoListForApplication } from '../../QuoteTypes';
import PaymentMethod from '../PaymentMethod';
import QuoteLinkPopup from '../QuoteLinkPopup';
import Popup from './Popup';

const { REACT_APP_BUY_NOW_TRACKING_URL } = getEnv();
export interface QuoteForDownload {
  quoteId: string;
  quoteNumber: string;
  lobType: string;
}

export interface BuyNowButtonProps {
  quoteId: string;
  /** Every quote in the group, so "Download Quote" can fetch a PDF per product. */
  quotesForDownload: QuoteForDownload[];
  primaryApplicationId: string;
  applicationGroupId: string;
  applyNewQuoteIds: (newQuoteIds: NewQuoteForApp[]) => void;
  showSendQuoteButton: boolean;
  country: string;
  businessInfo: BusinessInfo;
  disabled: boolean;
  additionalInsuredInfoListByAppId?: Record<
    string,
    AdditionalInsuredInfoListForApplication['additionalInsuredInfoList']
  >;
  validateAdditionalInsured: () => boolean;
  versionData: WorkflowVersion;
  isCustomerAgreementActive: boolean;
  isAuthenticated: boolean;
  isAuthProviderLoading: boolean;
  paymentGraphqlClient: PaymentGraphqlClient | null;
  transactionType: string;
  quoteExpireDate: string;
  refetchBusinessInfo?: () => void;
}

const FOXQUILT_TERMS_OF_USE = 'https://www.foxquilt.com/terms-of-use.html';
const FOXQUILT_PRIVACY_NOTICE = 'https://www.foxquilt.com/privacy-policy.html';

interface BuyNowButtonTrackingData {
  applicationId: string;
  munichQuoteId: string;
}
const buyNowButtonTracking = (
  buyNowButtonTrackingData: BuyNowButtonTrackingData
) => {
  try {
    fetch(REACT_APP_BUY_NOW_TRACKING_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(buyNowButtonTrackingData)
    });
  } catch (e) {
    // don't show error of this api call to client
  }
};

const BuyNowButton: React.FC<BuyNowButtonProps> = (props) => {
  const {
    quoteId,
    quotesForDownload,
    primaryApplicationId,
    applicationGroupId,
    applyNewQuoteIds,
    country,
    businessInfo,
    disabled,
    additionalInsuredInfoListByAppId,
    validateAdditionalInsured,
    versionData,
    isCustomerAgreementActive,
    isAuthenticated,
    isAuthProviderLoading,
    paymentGraphqlClient,
    transactionType,
    quoteExpireDate,
    refetchBusinessInfo
  } = props;
  const [showPopup, setShowPopup] = useState(false);
  const [displayModal, setDisplayModal] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);

  const isEndorsement = transactionType === 'Endorsement';
  const closePopup = () => {
    setShowPopup(false);
  };
  const context: ContextValue | null = useContext(Context);
  const setCustomerAgreementActive = context?.setCustomerAgreementActive;

  const buttonName = isEndorsement ? 'CHANGE POLICY' : 'BUY NOW';

  // Turn a base64 PDF into either a download or a new tab (broker / customer
  // agreement flow opens the PDF instead of downloading it).
  const saveOrOpenPdf = (base64Pdf: string, pdfFileName: string) => {
    // Decode base64 string to binary string
    const binaryString = atob(base64Pdf);
    // Create a byte array from the binary string
    const byteArray = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      byteArray[i] = binaryString.charCodeAt(i);
    }
    // Convert binary buffer to Blob
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    // Create object URL
    const url = window.URL.createObjectURL(blob);

    if (isCustomerAgreementActive) {
      window.open(url);
    } else {
      // Create a download link
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', pdfFileName); // File name
      document.body.appendChild(link);
      // Trigger click event to start download
      link.click();
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  };

  const downloadPdf = async () => {
    setIsPdfLoading(true);
    const ratingQuotingClient = new RatingQuotingClient(
      versionData.quoteBackendVersion
    );
    // We now have one quote per product (lobType), so fetch and download a PDF
    // for each quote concurrently. Each quote is isolated in its own try/catch:
    // if one quote's summary call fails we skip it and still generate the rest.
    await Promise.all(
      quotesForDownload.map(async (quote) => {
        try {
          const data = await ratingQuotingClient.generateQuotePdf(
            quote.quoteId
          );
          const pdfFileName = `Foxquilt_Quote_${businessInfo.companyName}_${quote.lobType}_${quote.quoteNumber}.pdf`;
          saveOrOpenPdf(data, pdfFileName);
        } catch (err) {
          // Skip this quote on failure; the other quotes are unaffected.
        }
      })
    );
    setIsPdfLoading(false);
  };

  const QuotePDF = (
    <button
      className="w-full mt-2 border-2 border-faded rounded-md bg-white hover:bg-background text-quartary font-semibold py-1 px-3 md:px-14 flex justify-center items-center disabled:cursor-wait"
      onClick={downloadPdf}
      disabled={isPdfLoading}
    >
      {isPdfLoading ? (
        <>
          <Spinner className="h-6 w-6 mr-2" />
          Loading...
        </>
      ) : !isCustomerAgreementActive ? (
        <>
          <img className="h-auto w-auto inline mr-2" src={Download} />
          Download Quote
        </>
      ) : (
        'View Quote PDF'
      )}
    </button>
  );

  const clientUI = (
    <>
      <button
        disabled={disabled}
        className="w-full bg-tertiary hover:bg-tertiary-dark border-tertiary-dark rounded-md uppercase pointer font-bold px-14 py-3 mb-2 text-mobileFont md:text-xl text-white confirm-buy"
        onClick={() => {
          // Validate the Additional Insured information
          const validationResult = validateAdditionalInsured();
          if (validationResult) {
            setShowPopup(true);
            buyNowButtonTracking({
              applicationId: primaryApplicationId,
              munichQuoteId: quoteId
            }); // TODO: rename it later
          } else {
            const additionalInsuredEle = document.getElementById(
              'additional-insured-container'
            );
            additionalInsuredEle
              ? additionalInsuredEle.scrollIntoView({
                  behavior: 'smooth'
                })
              : null;
          }
        }}
      >
        {buttonName}
      </button>
      <p className="text-center text-xs mt-2 mx-auto">
        By clicking {buttonName}, you agree to Foxquilt&apos;s{' '}
        <a
          className="small-underline text-secondary"
          href={FOXQUILT_TERMS_OF_USE}
          target="_blank"
          rel="noreferrer"
        >
          Terms of Use
        </a>{' '}
        and{' '}
        <a
          className="small-underline text-secondary"
          href={FOXQUILT_PRIVACY_NOTICE}
          target="_blank"
          rel="noreferrer"
        >
          Privacy Notice
        </a>
      </p>
      <p className="text-center mt-2 text-xs mb-2">
        Your coverage will not be effective until payment is confirmed
      </p>
      <PaymentMethod />
      <div className="border-gray-300 border-t-2 border-b-2 py-4">
        <button
          className="w-full border-2 border-faded rounded-md bg-white hover:bg-background text-quartary font-semibold py-1 px-3 md:px-14"
          onClick={() => setDisplayModal(true)}
        >
          <EmailOutlined className="mb-1 mr-2" />
          Send Quote
        </button>
        {!isEndorsement ? QuotePDF : null}
      </div>
    </>
  );

  const brokerUI = (
    <>
      <div className="policy-warning flex flex-row items-center">
        <Warning
          style={{ color: '#FFD64E' }}
          fontSize="large"
          className="mr-2"
        />
        <span data-testid="policy-warning">
          Coverage is <b>NOT</b> bound, and a policy will <b>NOT</b> be issued
          until both the application is signed <b>AND</b> payment received.
        </span>
      </div>
      <div className="border-gray-300 border-t-2 border-b-2 py-4">
        {!isEndorsement ? QuotePDF : null}
      </div>
    </>
  );

  return (
    <div className="confirm-pay-container lg:max-w-xs text-center sm:mt-0">
      {!isCustomerAgreementActive ? clientUI : brokerUI}
      {showPopup ? (
        <Popup
          closeAction={closePopup}
          quoteId={quoteId}
          businessInfo={businessInfo}
          transactionType={transactionType}
          quoteExpireDate={quoteExpireDate}
          primaryApplicationId={primaryApplicationId}
          applicationGroupId={applicationGroupId}
          applyNewQuoteIds={applyNewQuoteIds}
          country={country}
          additionalInsuredInfoListByAppId={additionalInsuredInfoListByAppId}
          setShowPopup={setShowPopup}
          setCustomerAgreementActive={setCustomerAgreementActive}
          isAuthenticated={isAuthenticated}
          isAuthProviderLoading={isAuthProviderLoading}
          paymentGraphqlClient={paymentGraphqlClient}
          versionData={versionData}
        />
      ) : null}

      <QuoteLinkPopup
        displayModal={displayModal}
        handleClose={() => setDisplayModal(false)}
        applicationId={primaryApplicationId}
        applicationGroupId={applicationGroupId}
        applyNewQuoteIds={applyNewQuoteIds}
        businessInfo={businessInfo}
        additionalInsuredInfoListByAppId={additionalInsuredInfoListByAppId}
        refetchBusinessInfo={refetchBusinessInfo}
      />
    </div>
  );
};

export default BuyNowButton;
